import { gql } from '@apollo/client';
import IconArrowDown from '@apollo/icons/default/IconArrowDown.svg';
import IconArrowUp from '@apollo/icons/default/IconArrowUp.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSchema from '@apollo/icons/default/IconSchema.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Badge, Collapse, IconButton, Spinner } from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { LinterRuleDescriptionMarkdownRenderer } from 'src/app/graph/checksPage/linterConfigPage/linterRuleDescriptionMarkdownRenderer/LinterRuleDescriptionMarkdownRenderer';
import { ExampleCode } from 'src/app/graph/checksPage/linterConfigPage/linterRulesCard/exampleCode/ExampleCode';
import { useSchemaFromHash } from 'src/app/graph/proposalLayout/proposalEditor/hooks/useSchemaFromHash';
import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { assertNotNullOrReturnDefault } from 'src/lib/assertNotNullOrReturnDefault';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  LintDiagnosticLevel,
  LinterRuleCategory,
  ViolationLevel,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { SafeMarkdown } from 'src/lib/safeCompileMarkdown';

import { SUPERGRAPH_SCHEMA_ID } from '../../../constants';
import {
  OverrideViolationButton,
  OverrideViolationButtonIgnoredRuleFragment,
  OverrideViolationButtonLintDiagnosticFragment,
} from '../../overrideViolationButton/OverrideViolationButton';

export { OverrideViolationButtonIgnoredRuleFragment };

export const CustomCheckViolationListFragment = gql`
  fragment CustomCheckViolationListFragment on CheckViolation {
    rule
    level
    coordinate
    message
    sourceLocations {
      end {
        byteOffset
        column
        line
      }
      start {
        byteOffset
        column
        line
      }
      subgraphName
    }
  }
`;
export const RuleViolationListLintDiagnosticFragment = gql`
  fragment RuleViolationListLintDiagnosticFragment on LintDiagnostic {
    rule
    level
    coordinate
    message
    sourceLocations {
      end {
        line
        column
        byteOffset
      }
      start {
        line
        column
        byteOffset
      }
      subgraphName
    }
    ...OverrideViolationButtonLintDiagnosticFragment
  }
  ${OverrideViolationButtonLintDiagnosticFragment}
`;

export type Violation = (
  | Omit<
      GraphQLTypes.RuleViolationListLintDiagnosticFragment,
      'sourceLocations'
    >
  | Omit<GraphQLTypes.CustomCheckViolationListFragment, 'sourceLocations'>
) & {
  sourceLocations: Array<
    | GraphQLTypes.RuleViolationListLintDiagnosticFragment['sourceLocations'][number]
    | NonNullable<
        GraphQLTypes.CustomCheckViolationListFragment['sourceLocations']
      >[number]
  > | null;
};

export const RuleViolationListLinterRuleLevelConfigurationFragment = gql`
  fragment RuleViolationListLinterRuleLevelConfigurationFragment on LinterRuleLevelConfiguration {
    description
    goodExampleCode
    badExampleCode
    level
    category
  }
`;

export const RuleViolationList = ({
  name,
  violations,
  canIgnoreLines,
  isCollapsed,
  setIsCollapsed,
  isSupergraph,
  subgraphName,
  rule,
  ignoredLinterViolations,
  activeSchemaCoordinates,
  onClickLine,
  proposedSubgraphs,
  proposedSupergraphSchema,
  graphId,
  showMessage,
}: {
  name: string;
  violations: Violation[];
  canIgnoreLines: boolean;
  isCollapsed?: boolean;
  setIsCollapsed?: () => void;
  isSupergraph: boolean;
  rule:
    | GraphQLTypes.RuleViolationListLinterRuleLevelConfigurationFragment
    | undefined;
  ignoredLinterViolations:
    | GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[]
    | undefined;
  subgraphName: string;
  activeSchemaCoordinates?: string[];
  onClickLine?: (violation: Violation) => void;
  proposedSubgraphs: Array<{
    name: string;
    hash: string | undefined;
    getSdl?: () => string | undefined;
  }> | null;
  proposedSupergraphSchema: {
    hash: string | null;
    getSdl?: () => string | undefined;
  };
  graphId: string;
  showMessage: boolean;
}) => {
  const shouldCollapse = isCollapsed !== undefined && !!setIsCollapsed;
  const description = rule?.description;
  const goodExampleCode = rule?.goodExampleCode ?? null;
  const badExampleCode = rule?.badExampleCode ?? null;
  const level =
    violations.find(
      (violation) => violation.level !== LintDiagnosticLevel.IGNORED,
    )?.level ?? rule?.level;

  const [ignoredViolations, nonIgnoredViolations] = useMemo(() => {
    const violationsWithFilteredLocations = violations.map((violation) => {
      return {
        ...violation,
        sourceLocations:
          violation.sourceLocations?.filter(
            (location) =>
              (location.subgraphName ?? SUPERGRAPH_SCHEMA_ID) === subgraphName,
          ) ?? null,
      };
    });

    return _.partition(
      violationsWithFilteredLocations,
      (violation) => violation.level === LintDiagnosticLevel.IGNORED,
    );
  }, [subgraphName, violations]);

  return (
    <div className="w-full rounded border border-silver">
      <div
        className={classnames('flex justify-between p-4', {
          'items-center': !description,
        })}
      >
        <div className="flex w-full">
          {(level === LintDiagnosticLevel.ERROR ||
            level === ViolationLevel.ERROR) && (
            <IconError className="mr-2 mt-1 text-icon-error" />
          )}
          {(level === LintDiagnosticLevel.WARNING ||
            level === ViolationLevel.WARNING) && (
            <IconWarning className="mr-2 mt-1 text-icon-warning" />
          )}
          {level === ViolationLevel.INFO && (
            <IconInfo className="mr-2 mt-1 text-icon-info" />
          )}

          <div className="flex w-0 flex-1 flex-col space-y-1">
            <div className="flex items-center space-x-2">
              <h3
                className="truncate text-base font-semibold text-secondary"
                title={name}
              >
                {name}
              </h3>
              <Badge className="ml-1">
                {violations.length}{' '}
                {format.pluralize(violations.length, 'Violation')}
              </Badge>
              {rule?.category === LinterRuleCategory.COMPOSITION && (
                <Tooltip placement="top" label="Composition rule">
                  <div className="flex min-w-4 cursor-pointer items-center rounded text-center text-sm font-semibold text-inverted">
                    <IconSchema className="ml-2 text-icon-primary" />
                  </div>
                </Tooltip>
              )}
            </div>

            {description && (
              <p className="text-secondary [&>*]:inline">
                <LinterRuleDescriptionMarkdownRenderer
                  descriptionMarkdown={description}
                />
                {(goodExampleCode || badExampleCode) && (
                  <Tooltip
                    label={
                      <ExampleCode
                        goodExampleCode={goodExampleCode}
                        badExampleCode={badExampleCode}
                      />
                    }
                    interactive
                    placement="end"
                    className="mx-2"
                  >
                    <IconInfo className="ml-2 align-[-2px]" />
                  </Tooltip>
                )}
              </p>
            )}
          </div>
        </div>
        {shouldCollapse && (
          <Tooltip
            label={
              isCollapsed ? 'Show Rule Violations' : 'Collapse Rule Violations'
            }
            placement="top"
            closeOnClick={false}
          >
            <IconButton
              aria-label={
                isCollapsed
                  ? 'Show Rule Violations'
                  : 'Collapse Rule Violations'
              }
              variant="secondary"
              onClick={setIsCollapsed}
            >
              {isCollapsed ? <IconArrowDown /> : <IconArrowUp />}
            </IconButton>
          </Tooltip>
        )}
      </div>
      <Collapse in={!isCollapsed}>
        <div className="border-t border-silver">
          <div className="divide-y divide-silver">
            {nonIgnoredViolations.map((violation) => {
              return (
                <ViolationLine
                  key={`${violation.rule}-${violation.coordinate}`}
                  violation={violation}
                  canIgnoreLines={canIgnoreLines}
                  ignoredLinterViolations={ignoredLinterViolations}
                  isSupergraph={isSupergraph}
                  subgraphName={subgraphName}
                  isAvailable={
                    !isMinLengthArray(0, activeSchemaCoordinates) ||
                    !violation.coordinate ||
                    activeSchemaCoordinates.includes(violation.coordinate)
                  }
                  proposedSubgraphs={proposedSubgraphs}
                  proposedSupergraphSchema={proposedSupergraphSchema}
                  isCollapsed={!!isCollapsed}
                  graphId={graphId}
                  showMessage={showMessage}
                  {...(onClickLine
                    ? {
                        onClick: () => onClickLine(violation),
                      }
                    : {})}
                />
              );
            })}
          </div>
          {isMinLengthArray(1, ignoredViolations) && (
            <>
              <div
                className={classnames(
                  isMinLengthArray(1, nonIgnoredViolations) && 'border-t',
                  'border-b border-silver px-4 py-2 text-xs font-semibold uppercase text-neutral',
                )}
              >
                Ignored Violations ({ignoredViolations.length})
              </div>
              <div className="divide-y divide-silver">
                {ignoredViolations.map((violation) => {
                  return (
                    <ViolationLine
                      key={`${violation.rule}-${violation.coordinate}`}
                      violation={violation}
                      canIgnoreLines={canIgnoreLines}
                      ignoredLinterViolations={ignoredLinterViolations}
                      isSupergraph={isSupergraph}
                      subgraphName={subgraphName}
                      isAvailable
                      proposedSubgraphs={proposedSubgraphs}
                      proposedSupergraphSchema={proposedSupergraphSchema}
                      isCollapsed={!!isCollapsed}
                      graphId={graphId}
                      showMessage={showMessage}
                      {...(onClickLine
                        ? {
                            onClick: () => onClickLine(violation),
                          }
                        : {})}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

const ViolationLine = ({
  violation,
  canIgnoreLines,
  ignoredLinterViolations,
  isSupergraph,
  subgraphName,
  isAvailable,
  onClick,
  proposedSubgraphs,
  proposedSupergraphSchema,
  isCollapsed,
  graphId,
  showMessage,
}: {
  violation: Violation;
  canIgnoreLines: boolean;
  ignoredLinterViolations?: GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[];
  isSupergraph: boolean;
  subgraphName: string;
  isAvailable: boolean;
  onClick?: () => void;
  proposedSubgraphs: Array<{
    name: string;
    hash: string | undefined;
    getSdl?: () => string | undefined;
  }> | null;
  proposedSupergraphSchema: {
    hash: string | null;
    getSdl?: () => string | undefined;
  };
  isCollapsed: boolean;
  graphId: string;
  showMessage: boolean;
}) => {
  const lineNumbers = useMemo(() => {
    return _.uniq(
      violation.sourceLocations?.map((location) => location?.start?.line ?? 0),
    );
  }, [violation.sourceLocations]);

  const subgraphDataByName = useMemo(
    () => Object.fromEntries(proposedSubgraphs?.map((s) => [s.name, s]) ?? []),
    [proposedSubgraphs],
  );

  const firstValidLocation = violation.sourceLocations?.filter(
    (
      s,
    ): s is typeof s & {
      start: NonNullable<NonNullable<(typeof s)['start']>>;
    } => s.start !== null,
  )[0];

  return (
    <>
      {showMessage && violation.message && (
        <div className="py-2 pl-4 text-secondary">
          <SafeMarkdown
            markdown={violation.message}
            options={{
              overrides: {
                a: {
                  props: {
                    className: 'underline',
                    target: '_blank',
                  },
                },
              },
            }}
          />
        </div>
      )}
      {!!firstValidLocation && (
        <PlainHtmlButton
          className={classnames(
            'flex w-full items-center justify-between bg-silver-lighter py-2',
            onClick
              ? 'focus-visible:focus-ring cursor-pointer hover:bg-silver-light focus-visible:mx-0.5'
              : 'pointer-events-none',
          )}
          onClick={onClick}
          aria-disabled={!onClick}
        >
          <div className="flex items-center">
            <div
              className={classnames(
                'flex w-12 items-center justify-center border-r border-primary text-center font-mono text-sm text-secondary',
                !isAvailable && 'opacity-50',
              )}
            >
              <Tooltip
                label={`Violation occurs on line${
                  lineNumbers.length > 1 ? 's' : ''
                } ${new Intl.ListFormat('en').format(
                  lineNumbers.map((v) => v.toString()),
                )} of the ${
                  isSupergraph
                    ? subgraphName !== SUPERGRAPH_SCHEMA_ID
                      ? 'subgraph'
                      : 'supergraph'
                    : ''
                } schema`}
                placement="top"
              >
                <div className="w-fit rounded px-2 py-1 hover:bg-blue-lightest">
                  {firstValidLocation.start.line}
                  {lineNumbers.length > 1 && (
                    <span className="inline-block size-1 rounded-full bg-blue align-text-top" />
                  )}
                </div>
              </Tooltip>
            </div>
            <div
              className={classnames(
                'mx-2 flex size-4 items-center justify-center rounded-sm bg-green-lighter font-mono text-sm',
                !isAvailable && 'opacity-50',
              )}
            >
              +
            </div>

            {violation.coordinate ? (
              <SourceLocationCodeBlock
                isAvailable={isAvailable}
                isIgnored={violation.level === LintDiagnosticLevel.IGNORED}
              >
                {violation.coordinate}
              </SourceLocationCodeBlock>
            ) : isCollapsed ? null : (
              //  TODO: we should show all of the locations if they exist
              [firstValidLocation].map((location) => {
                let schemaHash: string | undefined;
                let sdl: string | undefined;
                if (subgraphName !== SUPERGRAPH_SCHEMA_ID) {
                  const subgraphData =
                    subgraphDataByName[
                      assertNotNullOrReturnDefault(location.subgraphName, '')
                    ];
                  sdl = subgraphData?.getSdl?.();
                  schemaHash = subgraphData?.hash;
                } else {
                  sdl = proposedSupergraphSchema.getSdl?.();
                  const hash: string | null | undefined =
                    proposedSupergraphSchema.hash;
                  if (typeof sdl !== 'string') {
                    schemaHash =
                      assertNotNullOrReturnDefault(hash, '') || undefined;
                  }
                }

                return (
                  <SourceLocation
                    key={[
                      Object.values({ ...location.start, ...location.end }),
                    ].join()}
                    location={location}
                    schemaHash={schemaHash}
                    sdl={sdl}
                    isAvailable={isAvailable}
                    isIgnored={violation.level === LintDiagnosticLevel.IGNORED}
                    graphId={graphId}
                  />
                );
              })
            )}
            {!isAvailable && (
              <Tooltip
                label="This item is no longer available in your schema"
                placement="top"
                shouldWrapChildren
              >
                <IconInfo className="text-link" />
              </Tooltip>
            )}
          </div>
          {canIgnoreLines &&
            ignoredLinterViolations &&
            violation.__typename === 'LintDiagnostic' && (
              <OverrideViolationButton
                violation={violation}
                subgraphName={
                  subgraphName === SUPERGRAPH_SCHEMA_ID ? null : subgraphName
                }
                ignoredLinterViolations={ignoredLinterViolations}
                defaultOverrideButtonClassName="mr-4 pointer-events-auto"
                undoButtonClassName="mr-4 my-1"
              />
            )}
        </PlainHtmlButton>
      )}
    </>
  );
};

function SourceLocationCodeBlock({
  isIgnored,
  isAvailable,
  children,
}: {
  isIgnored: boolean;
  isAvailable: boolean;
  children: React.ReactNode;
}) {
  return (
    <code
      className={classnames(
        'font-mono text-sm text-black',
        isIgnored && 'line-through',
        !isAvailable && 'mr-2 opacity-50',
      )}
    >
      {children}
    </code>
  );
}

function SourceLocation({
  schemaHash,
  sdl,
  location,
  isIgnored,
  isAvailable,
  graphId,
}: {
  schemaHash: string | undefined;
  sdl: string | undefined;
  location: NonNullable<Violation['sourceLocations']>[number];
  isIgnored: boolean;
  isAvailable: boolean;
  graphId: string;
}) {
  const { data, loading } = useSchemaFromHash({
    graphId,
    schemaHash,
    skip: typeof sdl === 'string',
  });

  // TODO: should show multiple lines if they exist
  const firstLine = (sdl ?? data?.service?.doc?.source)
    ?.split('\n')
    ?.slice((location.start?.line ?? 0) - 1)[0];

  return (
    <SourceLocationCodeBlock isAvailable={isAvailable} isIgnored={isIgnored}>
      {loading ? <Spinner size="xs" /> : <>{firstLine}</>}
    </SourceLocationCodeBlock>
  );
}
